import { useFormikContext } from 'formik';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import { useDynamicFields } from '../../../utils/dynamicFields';
import { getAddressForSubmission, getInitialAddressValues } from '../../../utils/streetHouseNumber';
import Button from '../../atoms/Button';
import AddressForm from '../AddressForm';
import AddressSuggestions, { useAddressSuggestions } from '../AddressSuggestions';
import { showDefaultError } from '../Notifications';
import AddressLegend from './AddressLegend';

export type BillingAddressFormProps = {
  isEditingBillingAddress: boolean;
  setEditingBillingAddress: (isEditingBillingAddress: boolean) => void;
  setLoading: (isLoading: boolean) => void;
};

const BillingAddressForm = ({
  isEditingBillingAddress,
  setEditingBillingAddress,
  setLoading: setOuterLoading,
}: BillingAddressFormProps) => {
  const [isLoading, setInnerLoading] = useState(false);
  const { values, validateForm, setFieldTouched } = useFormikContext<{ billingAddress: any }>();
  const {
    setBillingAddress,
    checkoutState: { orderState },
  } = useCheckoutState();
  const isCollectionPoint = !!orderState.addresses?.shipping?.collectionPoint;

  const { submitDynamicFieldArea } = useDynamicFields();
  const { isAddressSuggestionsOpen, setAddressSuggestionsOpen, suggestAddresses, addressSuggestions, skipSuggestions } =
    useAddressSuggestions();

  const validateAndApply = async () => {
    const errors = await validateForm();
    if (errors.billingAddress) {
      Object.keys(values.billingAddress).forEach(key => setFieldTouched(`billingAddress.${key}`));
      return;
    }

    setOuterLoading(true);
    setInnerLoading(true);
    const _addressSuggestions = await suggestAddresses(values.billingAddress);
    if (_addressSuggestions) {
      setOuterLoading(false);
      setInnerLoading(false);
      return;
    }

    try {
      await setBillingAddress(getAddressForSubmission(values.billingAddress));
      await submitDynamicFieldArea(
        {
          area: 'billingAddress',
          context: 'billingAddress',
          conditionValues: { billingCountry: values.billingAddress.country },
        },
        values
      );
      setEditingBillingAddress(false);
    } catch (e) {
      console.error(e);
      showDefaultError();
    }
    setOuterLoading(false);
    setInnerLoading(false);
  };

  if (!isEditingBillingAddress && orderState.addresses?.billing) {
    return (
      <>
        <div className="option__form">
          <div className="billing-address-form">
            <Button
              className="billing-address-form__change-btn"
              type="button"
              onClick={() => setEditingBillingAddress(true)}
            >
              <FormattedMessage id="generic.change" />
            </Button>
            <AddressLegend addressValues={getInitialAddressValues(orderState.addresses.billing)} />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="option__form">
      {isAddressSuggestionsOpen && (
        <AddressSuggestions
          addresses={addressSuggestions}
          context="billingAddress"
          onClose={() => setAddressSuggestionsOpen(false)}
          onSubmit={validateAndApply}
          skipSuggestions={skipSuggestions}
        />
      )}
      <AddressForm
        headline={isCollectionPoint ? <FormattedMessage id="address.billingAddress" /> : undefined}
        context="billingAddress"
        availableCountries={config.availableCountries.billing?.map(({ iso2Code }) => iso2Code) ?? [config.country]}
      />
      <Button
        type="button"
        className="billing-address-form__apply-btn"
        onClick={validateAndApply}
        disabled={isLoading}
        isLoading={isLoading}
      >
        <FormattedMessage id="generic.apply" />
      </Button>
    </div>
  );
};

export default BillingAddressForm;
